import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, register, user } = useAuth();

  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (params.get("error") === "oauth") {
      toast.error("Google sign-in failed. Please try again.");
    }
  }, [params]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register(email, password, name);
        toast.success("Account created");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-slate-200 flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Left panel — brand */}
      <div className="hidden lg:flex w-1/2 relative border-r border-[#2A2F38] flex-col justify-between p-12">
        <Link to="/" data-testid="login-brand-link" className="w-fit">
          <Logo size="md" />
        </Link>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6A00] mb-4 font-semibold">
            Welcome
          </div>
          <h2
            className="text-3xl lg:text-5xl font-black text-white tracking-tighter leading-[1.05] max-w-lg"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Sign in. Upload plans. Get a real estimate.
          </h2>
          <p className="mt-6 text-slate-400 max-w-md leading-relaxed">
            Built for residential GCs, remodelers, and design-build firms who don't have time to
            wrestle Excel for every bid.
          </p>
        </div>
        <div className="text-xs text-slate-500" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          // Powered by TAKT.
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden mb-8 block" data-testid="login-mobile-brand">
            <Logo size="sm" tagline={null} />
          </Link>

          <h1
            className="text-3xl font-bold text-white tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="login-heading"
          >
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {mode === "login" ? "Resume estimating where you left off." : "Get started — no card required."}
          </p>

          <Button
            type="button"
            onClick={handleGoogle}
            className="w-full mt-8 bg-white hover:bg-slate-100 text-slate-900 font-medium rounded-md py-5"
            data-testid="google-signin-btn"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#2A2F38]" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-slate-500">or</span>
            <div className="flex-1 h-px bg-[#2A2F38]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                  Name
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                  placeholder="Alex Carpenter"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                placeholder="you@yourbuild.co"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                placeholder="At least 6 characters"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FF6A00] hover:bg-[#E65C00] text-white rounded-md py-5"
              data-testid="auth-submit-btn"
            >
              {(() => {
                if (submitting) return "Working…";
                return mode === "login" ? "Sign in" : "Create account";
              })()}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-400 text-center">
            {mode === "login" ? "Don't have an account?" : "Already have one?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#FF6A00] hover:text-[#E65C00] font-medium"
              data-testid="toggle-auth-mode-btn"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
